import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { RotateCcw, Trash2, Upload } from 'lucide-react';
import { Badge, Button, DropZone } from '@bliks-io/ui';
import {
  PARTNER_MATERIAL_CONTENT_TYPES,
  PARTNER_MATERIAL_FILE_FORMATS,
  partnerMaterialFormatOf,
  type PartnerMaterialFile,
  type PartnerMaterialLocale
} from '@bliks-io/contracts';
import {
  PartnerFileFormatBadge,
  PartnerFileFormatIcon
} from '@/components/partner-content/PartnerFileFormat';
import { PARTNER_MATERIAL_LOCALE_NAMES } from '@/lib/partner-material-text';
import { formatFileSize } from '@/lib/formatters';
import { FILE_REJECTION_KEYS, checkMaterialFile } from './materialForm';

const ACCEPT = PARTNER_MATERIAL_FILE_FORMATS.map(
  (format) => `.${format},${PARTNER_MATERIAL_CONTENT_TYPES[format]}`
).join(',');

interface MaterialFileSlotProps {
  locale: PartnerMaterialLocale;
  stored: PartnerMaterialFile | undefined;
  pendingUpload: File | undefined;
  pendingRemoval: boolean;
  onPick: (file: File) => void;
  onRemove: () => void;
  onUndo: () => void;
}

/** What the slot shows: the file picked for upload, else the stored one unless it is being removed. */
function shownFile(
  stored: PartnerMaterialFile | undefined,
  pendingUpload: File | undefined,
  pendingRemoval: boolean
) {
  if (pendingUpload) {
    return {
      name: pendingUpload.name,
      format: partnerMaterialFormatOf(pendingUpload.type),
      size: pendingUpload.size
    };
  }
  if (stored && !pendingRemoval) {
    return { name: stored.fileName, format: stored.format, size: stored.size };
  }
  return null;
}

/** The file of one language: what is stored, what will replace it, or a drop target. */
export function MaterialFileSlot({
  locale,
  stored,
  pendingUpload,
  pendingRemoval,
  onPick,
  onRemove,
  onUndo
}: MaterialFileSlotProps) {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const language = PARTNER_MATERIAL_LOCALE_NAMES[locale];

  const accept = (file: File | undefined) => {
    if (!file) return;
    const check = checkMaterialFile(file);
    if ('rejected' in check) {
      toast.error(t(FILE_REJECTION_KEYS[check.rejected]));
      return;
    }
    onPick(file);
  };

  const shown = shownFile(stored, pendingUpload, pendingRemoval);

  if (!shown) {
    return (
      <div className="flex flex-col gap-2">
        {pendingRemoval && (
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <span>
              {t('partnerContent.materials.files.removedOnSave', { name: stored?.fileName })}
            </span>
            <Button type="button" variant="ghost" size="small" onClick={onUndo}>
              <RotateCcw className="mr-2 h-3.5 w-3.5" />
              {t('partnerContent.actions.undo')}
            </Button>
          </div>
        )}
        <DropZone
          id={`material-file-${locale}`}
          accept={ACCEPT}
          // The size limit is checked above with a message; DropZone would drop it silently.
          maxSize={Number.POSITIVE_INFINITY}
          onChange={(files) => accept(files[0])}
          title={t('partnerContent.materials.files.dropTitle', { language })}
          description={t('partnerContent.materials.files.dropHint')}
        />
      </div>
    );
  }

  return (
    <div className="flex items-center gap-3 rounded-md border px-3 py-2">
      <PartnerFileFormatIcon format={shown.format ?? undefined} />
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium">{shown.name}</p>
        <p className="flex items-center gap-2 text-xs text-muted-foreground">
          {shown.format && <PartnerFileFormatBadge format={shown.format} />}
          {formatFileSize(shown.size)}
          {pendingUpload && (
            <Badge colorScheme="warning" size="sm">
              {t('partnerContent.materials.files.uploadOnSave')}
            </Badge>
          )}
        </p>
      </div>
      <input
        ref={inputRef}
        type="file"
        accept={ACCEPT}
        className="hidden"
        aria-label={t('partnerContent.materials.files.choose', { language })}
        onChange={(event) => {
          accept(event.target.files?.[0]);
          event.target.value = '';
        }}
      />
      {pendingUpload ? (
        <Button type="button" variant="ghost" size="small" onClick={onUndo}>
          <RotateCcw className="mr-2 h-3.5 w-3.5" />
          {t('partnerContent.actions.undo')}
        </Button>
      ) : (
        <>
          <Button
            type="button"
            variant="outline"
            size="small"
            onClick={() => inputRef.current?.click()}
          >
            <Upload className="mr-2 h-4 w-4" />
            {t('partnerContent.materials.files.replace')}
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            aria-label={t('partnerContent.materials.files.remove', { language })}
            onClick={onRemove}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </>
      )}
    </div>
  );
}
